import {ORDERS_PER_PAGE, OrderTableColumns} from "../AdminOrders/consts.jsx";
import {OrderStatus, RoundedTable, StyledButton} from "../../components/index.jsx";
import columns from "../AdminOrders/columns.jsx";
import React, {useCallback, useContext, useMemo, useState} from "react";
import {AppContext} from "../../AppContext.jsx";
import {HttpClient} from "../../http/HttpClient.jsx";
import {ConfigProvider, message} from "antd";
import {OrderChangesHistoryModal} from "../AdminOrders/OrderChangesHistoryModal.jsx";
import {openWindow} from "../../http/WindowOpener.jsx";
import {UpdateOrderStatusModal} from "../AdminOrders/UpdateOrderModal/index.js";
import {StringBuilder} from "../../AppUtils.js";
import {DeclineOrderModal} from "../../event/DeclineOrderModal/DeclineOrderModal.jsx";
import {SearchTopBar} from "./SearchTopBar.jsx";
import {useDebounceState, useRequest} from "../../utils/hooks.jsx";
import "./AdminOrdersManagement.css";
import {AdminOrderSearchProjection, DefaultAdminOrderSearchField} from "./consts.js";
import {transformOrders} from "../AdminOrders/utils.jsx";
import {acceptOrder, cancelOrder, deliveredOrder} from "../../api/orders/orders.js";
import {
    changeOrderStatus,
    createOrderIssueLog,
    updateOrder,
    updateOrderIssueLog,
    sendReminderToProvider as sendReminderToProviderApi
} from "../../api/orders/adminOrders.js";
import {updateBudgetTransactionAdminComment} from "../../api/budget/adminBudget.js";
import {isOrderHasShippingProvider} from "../../event/utils.jsx";
import {
    isExcelHomeDelivery,
    getToastMessageToExcelOrders,
    generateAdminOrderTableActions,
    generateProviderReminderOperationActions
} from "../AdminOrders/utils.jsx";
import {
    TableDateRangeTable,
    TableDateRangeTableAction
} from "../../components/OrdersDateRangeTable/TableDateRangeTable.jsx";
import {NOOP} from "../../utils/NOOP.jsx";
import {OrdersShortcuts} from "../../event/consts.js";
import {OrdersStatisticsLabel} from "../AdminOrders/OrdersStatisticsLabel.jsx";
import {TableDateSearchProvider} from "../Context/TableDateSearchContext.jsx";
import {OrderIssueLogsModal} from "../../event/IssueModal/OrderIssueLogsModal.jsx";

export const AdminOrdersManagement = () => {
    const {me, services} = useContext(AppContext);
    const [orders, setOrders] = useState([]);
    const [loadingOrders, setLoadingOrders] = useState(false);
    const [historyOrder, setHistoryOrder] = useState(null);
    const [updateOrderStatusInfo, setUpdateOrderStatusInfo] = useState(null);
    const [declineOrder, setDeclineOrder] = useState(null);
    const [issueModalOrder, setIssueModalOrder] = useState(null);

    const [searchInput, setSearchInput, unDebouncedSearchInput] = useDebounceState("", 500);
    const [searchField, setSearchField] = useState(DefaultAdminOrderSearchField);

    const [deliveryProviders] = useRequest("/admin/api/deliveryProviders");
    const [systemUsers] = useRequest("/admin/api/users/systemStaffUsers");

    const onEditOrder = useCallback(order => {
        openWindow(`/admin/events/${order.eventId}/edit`);
    }, []);

    const onSearch = useCallback(async (searchInput, searchField) => {
        setLoadingOrders(true);
        const ordersFromServer = await HttpClient.safePost("/admin/api/orders/byField", {
            searchInput,
            searchField,
            projection: AdminOrderSearchProjection
        });

        if (ordersFromServer?.error) {
            message.error("שגיאה בעת טעינת הזמנות");
        } else {
            setOrders(transformOrders(ordersFromServer));
        }

        setLoadingOrders(false);
    }, []);

    const onDeclineOrder = useCallback(
        async ({declineReason, declineComment}) => {
            try {
                const declinedOrder = await HttpClient.post(`/api/events/${declineOrder.eventId}/declineV2`, {
                    declineReason,
                    declineComment,
                    updatedAt: declineOrder?.updatedAt
                });

                if (declinedOrder.error) {
                    message.error(
                        new StringBuilder("שגיאה בעת סירוב ההזמנה").append(`(${declinedOrder.error})`).toString()
                    );
                } else {
                    message.success("ההזמנה סורבה בהצלחה");
                    getToastMessageToExcelOrders(OrderStatus.declined, declineOrder);
                }

                onSearch();
            } catch (e) {
                message.error(e.message, 3);
            }
            setDeclineOrder(null);
        },
        [declineOrder?.eventId, onSearch]
    );

    const onChangeOrderStatus = useCallback(
        async (event, status, toRemove) => {
            try {
                if (toRemove) {
                    await changeOrderStatus(event, status, toRemove);
                } else {
                    switch (status) {
                        case OrderStatus.accepted: {
                            await acceptOrder(event);
                            break;
                        }
                        case OrderStatus.declined: {
                            setDeclineOrder(event);
                            break;
                        }
                        case OrderStatus.delivered: {
                            await deliveredOrder(event);
                            break;
                        }
                        case OrderStatus.cancelled: {
                            await cancelOrder(event);
                            getToastMessageToExcelOrders(status, event);
                            break;
                        }
                        default: {
                            await changeOrderStatus(event, status, toRemove);
                            break;
                        }
                    }
                }

                onSearch();
            } catch (e) {
                message.error(e.message, 3);
            }
            setUpdateOrderStatusInfo(null);
        },
        [onSearch]
    );

    const onCommentSave = useCallback(
        async (order, commentText) => {
            const adminComment = commentText
                ? {
                      content: commentText,
                      author: me.firstName,
                      lastUpdate: Date.now()
                  }
                : null;

            const toUpdate = {
                eventId: order.eventId,
                adminComment
            };

            const {error} = order.isBudgetTransaction
                ? await updateBudgetTransactionAdminComment(
                      order,
                      adminComment,
                      "שגיאה, ההערה על ההזמנה לא נשמרה",
                      "ההערה על ההזמנה נשמרה בהצלחה"
                  )
                : await updateOrder(
                      toUpdate,
                      "none",
                      order.updatedAt,
                      "שגיאה, ההערה על ההזמנה לא נשמרה",
                      "ההערה על ההזמנה נשמרה בהצלחה"
                  );

            if (error) {
                return;
            }

            onSearch(searchInput, searchField);
        },
        [onSearch, me.firstName, searchInput, searchField]
    );

    const onSaveIssue = useCallback(
        async issue => {
            if (issue.logId) {
                await updateOrderIssueLog({...issue, eventId: issueModalOrder.eventId});
            } else {
                await createOrderIssueLog({...issue, eventId: issueModalOrder.eventId});
            }
            setIssueModalOrder(null);
            onSearch(searchInput, searchField);
        },
        [issueModalOrder, searchInput, searchField, onSearch]
    );

    const sendReminderToProvider = useCallback(
        async order => {
            try {
                const {error} = await sendReminderToProviderApi(order);
                if (!error) {
                    onSearch(searchInput, searchField); // Refresh to show updated count
                }
            } catch (e) {
                message.error(e.message, 3);
            }
        },
        [onSearch, searchInput, searchField]
    );

    const operationActions = useMemo(
        () => generateProviderReminderOperationActions(me, sendReminderToProvider, setDeclineOrder, setIssueModalOrder),
        [me, sendReminderToProvider, setDeclineOrder, setIssueModalOrder]
    );

    return (
        <>
            <ConfigProvider direction={"rtl"}>
                <DeclineOrderModal
                    visible={!!declineOrder}
                    onSave={onDeclineOrder}
                    onClose={() => setDeclineOrder(null)}
                />

                <UpdateOrderStatusModal
                    setUpdateOrderStatusInfo={setUpdateOrderStatusInfo}
                    updateOrderStatusInfo={updateOrderStatusInfo}
                    onChangeOrderStatus={onChangeOrderStatus}
                />

                <OrderChangesHistoryModal
                    visible={!!historyOrder}
                    orderId={historyOrder?.eventId}
                    onClose={() => setHistoryOrder(null)}
                    isAdmin={true}
                />

                <OrderIssueLogsModal
                    visible={!!issueModalOrder}
                    onClose={() => setIssueModalOrder(null)}
                    onSave={onSaveIssue}
                    order={issueModalOrder}
                />
            </ConfigProvider>

            <div className="admin-orders-management">
                <SearchTopBar
                    onSearch={onSearch}
                    searchInput={searchInput}
                    setSearchInput={setSearchInput}
                    unDebouncedSearchInput={unDebouncedSearchInput}
                    searchField={searchField}
                    setSearchField={setSearchField}
                />

                <ConfigProvider direction={"rtl"}>
                    <div className="admin-orders-management-table-container">
                        <TableDateSearchProvider localStorageKey="adminOrdersManagmentSearchDate">
                            <TableDateRangeTable
                                tableClassName="admin-orders-management-table"
                                data={orders}
                                showSearchByCreatedAt={false}
                                showDateRangeFilter={false}
                                actions={generateAdminOrderTableActions(
                                    me,
                                    onEditOrder,
                                    (order, status, toRemove) =>
                                        setUpdateOrderStatusInfo({
                                            event: order,
                                            status,
                                            toRemove
                                        }),
                                    order => setHistoryOrder(order),
                                    NOOP,
                                    NOOP,
                                    NOOP,
                                    NOOP,
                                    order => setIssueModalOrder(order),
                                    true
                                )}
                                hideColumns={[OrderTableColumns.actions]}
                                columns={columns(
                                    me,
                                    services,
                                    onEditOrder,
                                    (order, status, toRemove) =>
                                        setUpdateOrderStatusInfo({
                                            event: order,
                                            status,
                                            toRemove
                                        }),
                                    () => onSearch(searchInput, searchField),
                                    systemUsers,
                                    null,
                                    null,
                                    order => setHistoryOrder(order),
                                    null,
                                    true,
                                    null,
                                    null,
                                    onCommentSave,
                                    deliveryProviders,
                                    true,
                                    null,
                                    NOOP,
                                    NOOP,
                                    operationActions
                                )}
                                pagination={{
                                    position: ["bottomCenter"],
                                    showSizeChanger: false,
                                    defaultPageSize: ORDERS_PER_PAGE,
                                    showTotal: total => <span>סה״כ {total}</span>
                                }}
                                isLoadingOrderTable={loadingOrders}
                            />
                        </TableDateSearchProvider>
                    </div>
                </ConfigProvider>
            </div>
        </>
    );
};
